import { useState, type ReactNode } from 'react'
import { AlertTriangle, CalendarDays, Pencil, Plus, RefreshCw, Tags, Trash2 } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { deletePlan, fetchPlans, selectPlans } from '@/features/plans/plansSlice'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card, EmptyState, Spinner } from '@/components/ui/Surface'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { PlanFormModal } from './PlanFormModal'
import { formatCurrency, formatDate } from '@/lib/format'
import { useAsyncAction } from '@/lib/useAsyncAction'
import type { Plan } from '@/types'

export function PlansPage(): ReactNode {
  const dispatch = useAppDispatch()
  const plans = useAppSelector(selectPlans)
  const { loading, error } = useAppSelector((s) => s.plans)
  const { isPending, run } = useAsyncAction()

  const [form, setForm] = useState<{ open: boolean; editing: Plan | null }>({
    open: false,
    editing: null
  })
  const [deleting, setDeleting] = useState<Plan | null>(null)

  return (
    <>
      <PageHeader
        title="Plans"
        description="Subscription templates you assign to primary dealers."
        actions={
          <>
            <Button
              variant="secondary"
              icon={RefreshCw}
              loading={loading}
              onClick={() => void dispatch(fetchPlans())}
            >
              Refresh
            </Button>
            <Button
              variant="primary"
              icon={Plus}
              onClick={() => setForm({ open: true, editing: null })}
            >
              New plan
            </Button>
          </>
        }
      />

      {error ? (
        <Card className="border-negative/40 bg-negative/10 p-4">
          <p className="flex items-center gap-2 text-[12.5px] text-negative">
            <AlertTriangle size={14} aria-hidden />
            {error}
          </p>
        </Card>
      ) : loading && plans.length === 0 ? (
        <Card>
          <Spinner label="Loading plans" />
        </Card>
      ) : plans.length === 0 ? (
        <Card>
          <EmptyState
            icon={Tags}
            title="No plans yet"
            description="Create a plan so you can move dealers off trials and onto paid access."
            action={
              <Button
                variant="primary"
                size="sm"
                icon={Plus}
                onClick={() => setForm({ open: true, editing: null })}
              >
                Create the first plan
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {plans.map((plan) => (
            <Card
              key={plan.id}
              className="flex flex-col p-4 transition-colors hover:border-accent/40"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="truncate text-[14px] font-semibold text-fg">{plan.plan_name}</h2>
                  <p className="mt-0.5 font-mono text-[11px] text-subtle">ID {plan.id}</p>
                </div>
                {plan.is_active ? (
                  <Badge tone="green">Active</Badge>
                ) : (
                  <Badge tone="neutral">Inactive</Badge>
                )}
              </div>

              <p className="mt-4 text-[26px] font-semibold leading-none tracking-tight text-fg">
                {formatCurrency(plan.price)}
              </p>
              <p className="mt-2 flex items-center gap-1.5 text-[11.5px] text-muted">
                <CalendarDays size={12} className="text-accent" aria-hidden />
                {plan.duration_days} day{plan.duration_days === 1 ? '' : 's'} of access
              </p>

              <div className="mt-auto flex items-center justify-between gap-2 border-t border-line pt-3">
                <span className="text-[10.5px] text-subtle">Added {formatDate(plan.created_at)}</span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={Pencil}
                    aria-label={'Edit ' + plan.plan_name}
                    onClick={() => setForm({ open: true, editing: plan })}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={Trash2}
                    aria-label={'Delete ' + plan.plan_name}
                    className="text-negative hover:bg-negative/10 hover:text-negative"
                    onClick={() => setDeleting(plan)}
                  />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <PlanFormModal
        open={form.open}
        editing={form.editing}
        onClose={() => setForm({ open: false, editing: null })}
      />

      <ConfirmDialog
        open={deleting !== null}
        title="Delete this plan?"
        confirmLabel="Delete plan"
        loading={isPending('delete')}
        message={
          <p>
            <strong className="font-semibold text-fg">{deleting?.plan_name}</strong>{' '}
            will no longer be assignable. Dealers already on it keep their current access until it
            expires.
          </p>
        }
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return
          void run('delete', () =>
            dispatch(deletePlan({ id: deleting.id, name: deleting.plan_name })).unwrap()
          ).then(() => setDeleting(null))
        }}
      />
    </>
  )
}
